import { Lock, User, FileText, CheckCircle, ArrowLeft, LogOut, Image } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { User as SupabaseUser } from '@supabase/supabase-js';

import { supabase } from '../../lib/supabase';
import { useStudentOnboardingStore, StudentOnboardingStep } from '../../store/studentOnboarding.store';
import { PasswordStep } from './Student/PasswordStep';
import { AvatarStep } from './Student/AvatarStep';
import { ProfileStep } from './Student/ProfileStep';
import { EulaStep } from './Student/EulaStep';
import { CompleteStep } from './Student/CompleteStep';

const STEPS = [
  { id: 'password' as StudentOnboardingStep, label: 'Password', icon: Lock },
  { id: 'avatar' as StudentOnboardingStep, label: 'Photo', icon: Image },
  { id: 'profile' as StudentOnboardingStep, label: 'Profile', icon: User },
  { id: 'eula' as StudentOnboardingStep, label: 'Terms', icon: FileText },
  { id: 'complete' as StudentOnboardingStep, label: 'Complete', icon: CheckCircle },
];

export default function StudentOnboarding() {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, resetOnboarding, passwordSet, avatarSet, profileComplete, eulaAccepted } = useStudentOnboardingStore();
  const [loading, setLoading] = useState(true);
  const [_user, setUser] = useState<SupabaseUser | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }
      setUser(session.user);

      // Check if user already completed onboarding
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('profile_complete, eula_accepted_at')
        .eq('id', session.user.id)
        .single();

      if (profile) {
        const needsEula = !profile.eula_accepted_at;

        // If profile is complete and EULA is accepted, redirect to dashboard or app
        if (profile.profile_complete && !needsEula) {
          navigate('/student-onboarding/complete');
          return;
        }

        // If profile is complete but needs EULA, go to EULA step
        if (profile.profile_complete && needsEula) {
          setCurrentStep('eula');
        }
      }

      // Respect Zustand store progress — don't reset to an earlier step.
      // This prevents a race condition where auth state changes (e.g. after
      // updateUser({ password })) cause this effect to re-run and clobber the
      // current step that was already advanced by a step component.
      // The store is persisted to localStorage, so it survives re-renders.
      const store = useStudentOnboardingStore.getState();
      if (store.passwordSet && store.currentStep === 'password') {
        setCurrentStep('avatar');
      } else if (store.avatarSet && store.currentStep === 'avatar') {
        setCurrentStep('profile');
      } else if (store.profileComplete && store.currentStep === 'profile') {
        setCurrentStep('eula');
      } else if (store.eulaAccepted && store.currentStep === 'eula') {
        setCurrentStep('complete');
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate, setCurrentStep]);

  const handleSignOut = async () => {
    resetOnboarding();
    await supabase.auth.signOut();
    navigate('/');
  };

  const getCurrentStepIndex = () => {
    return STEPS.findIndex(s => s.id === currentStep);
  };

  const handlePasswordComplete = () => {
    setCurrentStep('avatar');
  };

  const handleAvatarComplete = () => {
    setCurrentStep('profile');
  };

  const handleProfileComplete = () => {
    setCurrentStep('eula');
  };

  const handleEulaComplete = () => {
    setCurrentStep('complete');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 'password':
        return <PasswordStep onNext={handlePasswordComplete} />;
      case 'avatar':
        return <AvatarStep onNext={handleAvatarComplete} />;
      case 'profile':
        return <ProfileStep onComplete={handleProfileComplete} />;
      case 'eula':
        return <EulaStep onComplete={handleEulaComplete} />;
      case 'complete':
        return <CompleteStep />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#111010] font-['Inter']">
      <Helmet>
        <title>Complete Your Profile - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>
      
      {/* Header */}
      <header className="border-b border-[#504A4A] bg-[#111010]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={20} className="text-[#A9A3A3]" />
            <img src="/rollops-logo.png" alt="RollOps" className="w-10 h-10" />
            <span className="text-2xl font-bold text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Progress Tabs */}
      <div className="border-b border-[#504A4A] bg-[#111010]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-2 overflow-x-auto">
            {STEPS.map((step, index) => {
              const currentIndex = getCurrentStepIndex();
              const isActive = step.id === currentStep;
              const isCompleted = index < currentIndex || 
                (step.id === 'password' && passwordSet) || 
                (step.id === 'avatar' && avatarSet) ||
                (step.id === 'profile' && profileComplete) || 
                (step.id === 'eula' && eulaAccepted);
              const isAccessible = index <= currentIndex;
              const Icon = step.icon;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isAccessible && setCurrentStep(step.id)}
                    disabled={!isAccessible}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-[#9B1421] text-white'
                        : isCompleted
                        ? 'bg-green-600/20 text-green-500 hover:bg-green-600/30'
                        : 'bg-[#363232] text-[#A9A3A3]'
                    } ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                  >
                    <Icon size={16} />
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        isCompleted ? 'bg-green-600' : 'bg-[#504A4A]'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {renderStep()}
      </main>
    </div>
  );
}
